// @ts-nocheck
/**
 * SettingsModal Component
 * 
 * Settings modal with auto-save toggle, admin tools, and sign out.
 * Extracted from App.tsx to reduce file size.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../../services/firebaseConfig';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import type { MasterProfile } from '../../types';
import type { MonetizationMode } from '../../config/adminConfig';
import { isAdmin, INHOUSE_SELLING_ENABLED } from '../../config/adminConfig';
import { getAuthHeaders, authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { SUPPORTED_LANGUAGES } from '../../i18n';

/** Display labels for the language selector, keyed by BCP-47 code. */
const LANGUAGE_LABELS: Record<string, string> = {
  en: '🇺🇸 English',
  ko: '🇰🇷 한국어',
  'zh-CN': '🇨🇳 中文',
  'pt-BR': '🇧🇷 Português',
  es: '🇪🇸 Español',
  de: '🇩🇪 Deutsch',
  fr: '🇫🇷 Français',
  it: '🇮🇹 Italiano',
  nl: '🇳🇱 Nederlands',
  sv: '🇸🇪 Svenska',
  pl: '🇵🇱 Polski',
};
import { clearAuthTimestamp } from '../../services/reauthService';
import { SUPPORTED_REGIONS, REGION_CONFIG, getRegionConfig } from '../../config/affiliateConfig';
import type { AnalysisProvider } from './FaceGuidelineModal';
import FeedbackForm from '../feedback/FeedbackForm';

/** Delete account confirmation modal state */
interface DeleteAccountState {
  showConfirm: boolean;
  confirmEmail: string;
  loading: boolean;
  error: string | null;
}

interface SettingsModalProps {
  language: string;
  masterProfile?: MasterProfile | null;
  autoSave: boolean;
  autoSearchEnabled: boolean;
  useRagRecommendation: boolean;
  monetizationMode: MonetizationMode;
  analysisProvider: AnalysisProvider;
  onLanguageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onAnalysisProviderChange: (provider: AnalysisProvider) => void;
  onAutoSaveChange: (value: boolean) => void;
  onAutoSearchChange: (value: boolean) => void;
  onRagRecommendationChange: (value: boolean) => void;
  onMonetizationModeChange: (mode: MonetizationMode) => void;
  onShowInventory: () => void;
  onShowOrderHistory: () => void;
  onClose: () => void;
  hydrateProfile: (uid: string, forceServer?: boolean) => Promise<void>;
  buildTimestamp?: string;
}

/**
 * Settings modal component
 */
function SettingsModal({
  language,
  masterProfile,
  autoSave,
  autoSearchEnabled,
  useRagRecommendation,
  monetizationMode,
  analysisProvider,
  hydrateProfile,
  onLanguageChange,
  onAnalysisProviderChange,
  onAutoSaveChange,
  onAutoSearchChange,
  onRagRecommendationChange,
  onMonetizationModeChange,
  onShowInventory,
  onShowOrderHistory,
  onClose,
  buildTimestamp,
}: SettingsModalProps) {
  // i18n translation hook
  const { t, i18n } = useTranslation('modals');

  // CSV Upload state (Admin only)
  const [csvUploadStatus, setCsvUploadStatus] = useState<{
    loading: boolean;
    message: string | null;
    success: boolean | null;
  }>({ loading: false, message: null, success: null });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Feedback form state
  const [showFeedback, setShowFeedback] = useState(false);

  // Country & Currency — multi-region support (Phase 1 geo-routing #210)
  const initialCountry = masterProfile?.identity?.country || 'US';
  const initialCurrency = masterProfile?.identity?.currency || 'USD';
  const [country, setCountry] = useState<string>(initialCountry);
  const [currency, setCurrency] = useState<string>(initialCurrency);
  const [profileError, setProfileError] = useState<string | null>(null);
  const prevCountryRef = useRef<string>(initialCountry);

  // Sync from masterProfile prop when it changes (e.g. after hydrateProfile)
  useEffect(() => {
    const c = masterProfile?.identity?.country || 'US';
    const cur = masterProfile?.identity?.currency || 'USD';
    setCountry(c);
    setCurrency(cur);
  }, [masterProfile?.identity?.country, masterProfile?.identity?.currency]);

  // Close modal on Escape key (consistent with BottomSheet, ProductExperiencePanel)
  // Skipped when a child dialog (e.g. FeedbackForm) is open.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showFeedback) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showFeedback]);

  /** Save country change to Firestore — guard auth first, then optimistic update */
  const handleCountryChange = async (newCountry: string) => {
    const user = auth.currentUser;
    if (!user) return;

    prevCountryRef.current = country;
    const regionCfg = getRegionConfig(newCountry);
    setCountry(newCountry);
    setCurrency(regionCfg.currency);
    setProfileError(null);

    try {
      const profileRef = doc(db, 'masterProfiles', user.uid);
      await updateDoc(profileRef, {
        'identity.country': newCountry,
        'identity.currency': regionCfg.currency,
        'meta.updatedAt': serverTimestamp(),
      });
      await hydrateProfile(user.uid);
    } catch (err) {
      console.error('Failed to update country:', err);
      setProfileError(t('settings.updateFailed', { defaultValue: 'Failed to save country' }));
      setCountry(prevCountryRef.current);
      setCurrency(getRegionConfig(prevCountryRef.current).currency);
    }
  };

  // Delete account state
  const [deleteAccount, setDeleteAccount] = useState<DeleteAccountState>({
    showConfirm: false,
    confirmEmail: '',
    loading: false,
    error: null,
  });

  const handleSignOut = async () => {
    try {
      clearAuthTimestamp();
      try { localStorage.removeItem('heseda-ai-language-explicit'); } catch { /* */ }
      await auth.signOut();
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
      alert(t('settings.signOutError'));
    }
  };

  /**
   * Handle account deletion
   */
  const handleDeleteAccount = async () => {
    if (!auth.currentUser) return;

    // Validate email confirmation
    if (deleteAccount.confirmEmail.toLowerCase() !== auth.currentUser.email?.toLowerCase()) {
      setDeleteAccount(prev => ({
        ...prev,
        error: t('deleteAccount.emailMismatch'),
      }));
      return;
    }

    setDeleteAccount(prev => ({ ...prev, loading: true, error: null }));

    try {
      const apiUrl = `${getApiBaseUrl()}/deleteUserAccount`;
      const response = await authFetch(apiUrl, {
        method: 'DELETE',
        body: JSON.stringify({
          confirmEmail: deleteAccount.confirmEmail,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Clear local state and sign out
        clearAuthTimestamp();
        try { localStorage.removeItem('heseda-ai-language-explicit'); } catch { /* */ }
        await auth.signOut();
        alert(t('deleteAccount.success'));
        onClose();
        // Reload to reset app state
        window.location.reload();
      } else {
        setDeleteAccount(prev => ({
          ...prev,
          loading: false,
          error: result.error || t('deleteAccount.error'),
        }));
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteAccount(prev => ({
        ...prev,
        loading: false,
        error: t('deleteAccount.error'),
      }));
    }
  };

  /**
   * Handle CSV file upload for catalog
   */
  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setCsvUploadStatus({
        loading: false,
        message: t('settings.selectCsvError'),
        success: false,
      });
      return;
    }

    setCsvUploadStatus({ loading: true, message: null, success: null });

    try {
      // Read file content
      const csvContent = await file.text();
      
      // Send to API for validation
      const headers = await getAuthHeaders();
      const uploadUrl = `${getApiBaseUrl()}/uploadCatalog`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          csvContent,
          vendorId: 'inhouse',
          catalogType: 'inhouse',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setCsvUploadStatus({
          loading: false,
          message: t('settings.uploadSuccess', { count: result.stats?.totalProducts, instructions: result.instructions?.[0] || '' }),
          success: true,
        });
      } else {
        setCsvUploadStatus({
          loading: false,
          message: `❌ ${result.errors?.[0] || result.message}`,
          success: false,
        });
      }
    } catch (error: any) {
      console.error('CSV upload error:', error);
      setCsvUploadStatus({
        loading: false,
        message: t('settings.uploadFailed'),
        success: false,
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content settings-modal"
        data-testid="settings-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
      >
        <div className="modal-header">
          <h3 id="settings-modal-title">{t('settings.title')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh' }}>
          
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 1: AI & Analysis Settings */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="settings-section">
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('settings.aiAnalysis')}
            </h4>

            {/* AI Provider Selection */}
            <div className="setting-item">
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.analysisEngine')}</strong>
                  <small>{t('settings.analysisEngineDescription')}</small>
                </span>
              </label>
              <div className="ai-provider-buttons" style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => onAnalysisProviderChange('gpt-vision')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: analysisProvider === 'gpt-vision' ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: analysisProvider === 'gpt-vision' ? '#faf5ff' : '#fff',
                    color: analysisProvider === 'gpt-vision' ? '#7c3aed' : '#374151',
                    fontWeight: analysisProvider === 'gpt-vision' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  🧠 GPT
                </button>
                <button
                  onClick={() => onAnalysisProviderChange('ailabtools')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: analysisProvider === 'ailabtools' ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: analysisProvider === 'ailabtools' ? '#faf5ff' : '#fff',
                    color: analysisProvider === 'ailabtools' ? '#7c3aed' : '#374151',
                    fontWeight: analysisProvider === 'ailabtools' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  🔬 AILabTools
                </button>
              </div>
            </div>

            {/* RAG Recommendation toggle */}
            <div className="setting-item">
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.recommendationMode')}</strong>
                  <small>{t('settings.recommendationModeDescription')}</small>
                </span>
              </label>
              <div className="ai-provider-buttons" style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => onRagRecommendationChange(true)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: useRagRecommendation ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: useRagRecommendation ? '#faf5ff' : '#fff',
                    color: useRagRecommendation ? '#7c3aed' : '#374151',
                    fontWeight: useRagRecommendation ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  📊 {t('settings.smartAI', 'Smart AI')}
                </button>
                <button
                  onClick={() => onRagRecommendationChange(false)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: !useRagRecommendation ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: !useRagRecommendation ? '#faf5ff' : '#fff',
                    color: !useRagRecommendation ? '#7c3aed' : '#374151',
                    fontWeight: !useRagRecommendation ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  📝 {t('settings.traditional')}
                </button>
              </div>
            </div>

            {/* Product Source toggle - only when selling features enabled */}
            {INHOUSE_SELLING_ENABLED && (
            <div className="setting-item">
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.productSource')}</strong>
                  <small>{t('settings.productSourceDescription')}</small>
                </span>
              </label>
              <div className="ai-provider-buttons" style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => onMonetizationModeChange('inhouse')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: monetizationMode === 'inhouse' ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: monetizationMode === 'inhouse' ? '#faf5ff' : '#fff',
                    color: monetizationMode === 'inhouse' ? '#7c3aed' : '#374151',
                    fontWeight: monetizationMode === 'inhouse' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  🏷️ Heseda
                </button>
                <button
                  onClick={() => onMonetizationModeChange('affiliate')}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: monetizationMode === 'affiliate' ? '2px solid #a855f7' : '1px solid #e5e7eb',
                    backgroundColor: monetizationMode === 'affiliate' ? '#faf5ff' : '#fff',
                    color: monetizationMode === 'affiliate' ? '#7c3aed' : '#374151',
                    fontWeight: monetizationMode === 'affiliate' ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  🛒 {t('settings.affiliate')}
                </button>
              </div>
            </div>
            )}

            {/* Auto-search toggle */}
            <div className="setting-item">
              <label className="setting-label">
                <input 
                  type="checkbox" 
                  checked={autoSearchEnabled} 
                  onChange={(e) => onAutoSearchChange(e.target.checked)} 
                />
                <span className="setting-text">
                  <strong>{t('settings.autoSearch')}</strong>
                  <small>{t('settings.autoSearchDescription')}</small>
                </span>
              </label>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 2: General Settings */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="settings-section" style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('settings.general')}
            </h4>
            
            {/* Language Selection */}
            <div className="setting-item">
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.language')}</strong>
                  <small>{t('settings.languageDescription')}</small>
                </span>
              </label>
              <select
                value={i18n.language}
                onChange={onLanguageChange}
                className="settings-select"
                data-testid="lang-select"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  marginTop: '8px',
                  cursor: 'pointer'
                }}
              >
                {SUPPORTED_LANGUAGES.map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGE_LABELS[code] ?? code}
                  </option>
                ))}
              </select>
            </div>

            {/* Country Selection — multi-region (Phase 1 #210) */}
            <div className="setting-item" style={{ marginTop: '12px' }}>
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.country') || 'Country'}</strong>
                  <small>{t('settings.countryDescription') || ''}</small>
                </span>
              </label>
              <select
                value={country}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="settings-select"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  marginTop: '8px',
                  cursor: 'pointer',
                }}
              >
                {SUPPORTED_REGIONS.map((r) => {
                  const cfg = REGION_CONFIG[r];
                  return (
                    <option key={r} value={r}>
                      {cfg.flag} {language === 'ko' ? cfg.labelKo : cfg.label}
                    </option>
                  );
                })}
              </select>
              {profileError && (
                <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>{profileError}</small>
              )}
            </div>

            {/* Currency — auto-set from country (only visible when in-house selling enabled) */}
            {INHOUSE_SELLING_ENABLED && (
            <div className="setting-item" style={{ marginTop: '12px', opacity: 0.5 }}>
              <label className="setting-label">
                <span className="setting-text">
                  <strong>{t('settings.currency') || 'Currency'}</strong>
                  <small>{t('settings.currencyAutoSet', { defaultValue: 'Auto-set by country' })}</small>
                </span>
              </label>
              <select
                value={currency}
                className="settings-select"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  marginTop: '8px',
                  cursor: 'not-allowed',
                  backgroundColor: '#f3f4f6',
                  color: '#9ca3af',
                }}
                disabled
              >
                {SUPPORTED_REGIONS.map((r) => {
                  const cfg = REGION_CONFIG[r];
                  return (
                    <option key={cfg.currency} value={cfg.currency}>
                      {cfg.currencySymbol} ({cfg.currency})
                    </option>
                  );
                })}
              </select>
            </div>
            )}

            {/* Auto-save toggle */}
            <div className="setting-item">
              <label className="setting-label">
                <input 
                  type="checkbox" 
                  checked={autoSave} 
                  onChange={(e) => onAutoSaveChange(e.target.checked)} 
                />
                <span className="setting-text">
                  <strong>{t('settings.autoSave')}</strong>
                  <small>{t('settings.autoSaveDescription')}</small>
                </span>
              </label>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 3: Help & Support */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="settings-section" style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('settings.helpSupport')}
            </h4>

            <div className="setting-item" style={{ marginBottom: '8px' }}>
              <button
                onClick={() => setShowFeedback(true)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: '#f0f9ff',
                  color: '#0369a1',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              >
                {t('settings.contactUs')}
              </button>
              <small style={{ display: 'block', marginTop: '4px', color: '#9ca3af', fontSize: '12px' }}>
                {t('settings.contactUsDescription')}
              </small>
            </div>
          </div>

          {/* Feedback form modal */}
          {showFeedback && (
            <FeedbackForm
              onClose={() => setShowFeedback(false)}
            />
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 4: My Account */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {auth.currentUser && (
            <div className="settings-section" style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('settings.myAccount')}
              </h4>

              {/* Order History Button - only when selling features enabled */}
              {INHOUSE_SELLING_ENABLED && (
              <div className="setting-item">
                <button 
                  className="btn btn--success"
                  onClick={() => {
                    onShowOrderHistory();
                    onClose();
                  }}
                  style={{ width: '100%' }}
                >
                  {t('settings.orderHistory')}
                </button>
              </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 5: Admin Tools (Admin Only) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {auth.currentUser && isAdmin(auth.currentUser.email) && (
            <div className="settings-section" style={{ borderTop: '1px solid #fef3c7', paddingTop: '16px', marginTop: '16px', backgroundColor: '#fffbeb', borderRadius: '8px', padding: '16px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('settings.adminTools')}
              </h4>
              
              {/* Admin Dashboard Button */}
              <div className="setting-item">
                <button 
                  className="btn btn--secondary"
                  onClick={() => {
                    onClose();
                    window.location.href = '/admin';
                  }}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  {t('settings.adminDashboard')}
                </button>
              </div>

              {/* Inventory Management */}
              <div className="setting-item">
                <button 
                  className="btn btn--primary"
                  onClick={() => {
                    onShowInventory();
                    onClose();
                  }}
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  {t('settings.inventoryManagement')}
                </button>
              </div>

              {/* CSV Catalog Upload */}
              <div className="setting-item">
                <label className="setting-label" style={{ display: 'block', marginBottom: '8px' }}>
                  <span className="setting-text">
                    <strong>{t('settings.uploadCatalog')}</strong>
                    <small>{t('settings.uploadCatalogDescription')}</small>
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  style={{ display: 'none' }}
                  id="csv-upload-input"
                />
                <button
                  className="btn btn--secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={csvUploadStatus.loading}
                  style={{ width: '100%' }}
                >
                  {csvUploadStatus.loading 
                    ? t('settings.validating')
                    : t('settings.selectCsvFile')
                  }
                </button>
                {csvUploadStatus.message && (
                  <small style={{ 
                    display: 'block', 
                    marginTop: '8px', 
                    color: csvUploadStatus.success ? '#16a34a' : '#dc2626',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {csvUploadStatus.message}
                  </small>
                )}
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION: About / Version */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="settings-section" style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginTop: '16px' }}>
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', lineHeight: 1.6 }}>
              <span>Heseda AI v{__APP_VERSION__}</span>
              {buildTimestamp && <span> · {buildTimestamp}</span>}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTION 6: Sign Out & Account Management */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {auth.currentUser && (
            <div className="settings-section" style={{ borderTop: '1px solid #f3f4f6', paddingTop: '16px', marginTop: '16px' }}>
              {/* Sign Out Button */}
              <button 
                className="signout-button"
                onClick={handleSignOut}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#f44336'}
              >
                {t('settings.signOut')}
              </button>
              <small style={{ display: 'block', marginTop: '8px', color: '#666', textAlign: 'center' }}>
                {auth.currentUser?.email}
              </small>

              {/* Delete Account - Danger Zone */}
              <div style={{ marginTop: '24px' }}>
                {!deleteAccount.showConfirm ? (
                  <button
                    onClick={() => setDeleteAccount(prev => ({ ...prev, showConfirm: true }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: 'transparent',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#fef2f2';
                      (e.target as HTMLButtonElement).style.borderColor = '#f87171';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                      (e.target as HTMLButtonElement).style.borderColor = '#fecaca';
                    }}
                  >
                    {t('settings.deleteAccount')}
                  </button>
                ) : (
                  <div style={{ 
                    backgroundColor: '#fef2f2', 
                    padding: '16px', 
                    borderRadius: '8px',
                    border: '1px solid #fecaca'
                  }}>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '14px', 
                      color: '#991b1b',
                      fontWeight: '600'
                    }}>
                      ⚠️ {t('deleteAccount.warning')}
                    </p>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '13px', 
                      color: '#7f1d1d' 
                    }}>
                      {t('deleteAccount.description')}
                    </p>
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '13px', 
                      color: '#7f1d1d' 
                    }}>
                      {t('deleteAccount.confirmEmail')}
                    </p>
                    <input
                      type="email"
                      value={deleteAccount.confirmEmail}
                      onChange={(e) => setDeleteAccount(prev => ({ 
                        ...prev, 
                        confirmEmail: e.target.value,
                        error: null 
                      }))}
                      placeholder={auth.currentUser?.email || ''}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: deleteAccount.error ? '1px solid #f87171' : '1px solid #d1d5db',
                        fontSize: '14px',
                        marginBottom: '8px',
                        boxSizing: 'border-box',
                      }}
                      disabled={deleteAccount.loading}
                    />
                    {deleteAccount.error && (
                      <p style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '12px', 
                        color: '#dc2626' 
                      }}>
                        {deleteAccount.error}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setDeleteAccount({ 
                          showConfirm: false, 
                          confirmEmail: '', 
                          loading: false, 
                          error: null 
                        })}
                        disabled={deleteAccount.loading}
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: deleteAccount.loading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t('deleteAccount.cancel')}
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteAccount.loading || !deleteAccount.confirmEmail}
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: deleteAccount.loading ? '#fca5a5' : '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (deleteAccount.loading || !deleteAccount.confirmEmail) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {deleteAccount.loading 
                          ? t('deleteAccount.deleting')
                          : t('deleteAccount.deleteButton')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
